import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { WaveformPeaks, formatAudioTime } from './core/peaks';
import {
  WAVEFORM_BAR_DEFAULTS,
  WAVEFORM_COLORS,
  drawWaveformBars,
  drawPlaceholderBars,
  setupWaveformCanvas,
} from './core/waveformRender';

/**
 * 統一波形レンダラー（自前 Canvas）。
 *
 * 事前計算済み peaks（min/max ペア）をバー描画する。
 * - 要素幅の変化に ResizeObserver で追従（純関数再描画のためリサイズ安定）
 * - 再生進捗の 2 色塗り分け
 * - クリック / ドラッグシーク（onSeek 指定時）
 * - ホバー位置の縦線 + 時刻ラベル（showHoverIndicator 指定時）
 * - peaks 未取得時のプレースホルダーバー（レイアウトシフトなしの即描画）
 */

export interface WaveformCanvasProps {
  peaks: WaveformPeaks | null;
  /** 再生進捗の現在秒数 */
  currentTime?: number;
  /** メディア総尺（秒）。進捗・シーク・ホバー時刻の計算に使用 */
  duration?: number;
  /** 描画高さ（px）。既定 48 */
  height?: number;
  /** 'mono': 全チャンネルを外包絡で 1 レーンに / 'split': チャンネルごとに縦積み */
  channelMode?: 'mono' | 'split';
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  waveColor?: string;
  progressColor?: string;
  /** クリック / ドラッグでのシーク（秒）。指定時のみインタラクティブになる */
  onSeek?: (_seconds: number) => void;
  /** ホバー位置の縦線 + 時刻ラベルを表示する */
  showHoverIndicator?: boolean;
  /** peaks 未取得時にプレースホルダーバーを描画する（既定 true） */
  showPlaceholder?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Container = styled.div<{ $height: number; $interactive: boolean }>`
  position: relative;
  width: 100%;
  height: ${p => p.$height}px;
  ${p => p.$interactive && `
    cursor: pointer;
    touch-action: none;
  `}
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  display: block;
  pointer-events: none;
`;

const HoverLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--soremo-blue, #009ace);
  pointer-events: none;
  z-index: 1;
`;

const HoverLabel = styled.span.attrs({
  className: 'bodytext-11',
})`
  position: absolute;
  top: 0;
  padding: 0 3px;
  background: var(--soremo-blue, #009ace);
  color: #fff;
  border-radius: 2px;
  line-height: 140%;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1;
`;

const WaveformCanvas = memo<WaveformCanvasProps>(({
  peaks,
  currentTime = 0,
  duration = 0,
  height = 48,
  channelMode = 'mono',
  barWidth = WAVEFORM_BAR_DEFAULTS.barWidth,
  barGap = WAVEFORM_BAR_DEFAULTS.barGap,
  barRadius = WAVEFORM_BAR_DEFAULTS.barRadius,
  waveColor = WAVEFORM_COLORS.wave,
  progressColor = WAVEFORM_COLORS.progress,
  onSeek,
  showHoverIndicator = false,
  showPlaceholder = true,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);
  const [width, setWidth] = useState(0);
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);

  const interactive = !!onSeek;
  const progressRatio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;

  // 要素幅の追従（リサイズ・レイアウト変化で自動再描画）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(prev => (Math.abs(prev - w) < 1 ? prev : w));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // 描画本体（peaks / 幅 / 進捗などの変化ごとに全再描画。数百バー程度なので十分軽い）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    const ctx = setupWaveformCanvas(canvas, width, height);
    if (!ctx) return;

    const barStyle = { barWidth, barGap, barRadius };

    if (!peaks || peaks.length === 0) {
      if (showPlaceholder) {
        drawPlaceholderBars(ctx, { x: 0, y: 0, width, height, color: waveColor, ...barStyle });
      }
      return;
    }

    const lanes: number[][][] =
      channelMode === 'split' && peaks.length > 1
        ? peaks.map(ch => [ch])
        : [peaks];
    const laneHeight = height / lanes.length;

    const drawAll = (color: string) => {
      lanes.forEach((laneChannels, i) => {
        drawWaveformBars(ctx, {
          channels: laneChannels,
          x: 0,
          y: i * laneHeight,
          width,
          height: laneHeight,
          color,
          ...barStyle,
        });
      });
    };

    drawAll(waveColor);

    const progressX = progressRatio * width;
    if (progressX > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, progressX, height);
      ctx.clip();
      drawAll(progressColor);
      ctx.restore();
    }
  }, [
    peaks, width, height, channelMode,
    barWidth, barGap, barRadius,
    waveColor, progressColor, progressRatio, showPlaceholder,
  ]);

  // --- ポインター操作（シーク + ホバー） ---

  const ratioFromClientX = useCallback((clientX: number): number => {
    const container = containerRef.current;
    if (!container) return 0;
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!onSeek || duration <= 0) return;
    e.preventDefault();
    draggingRef.current = true;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    onSeek(ratioFromClientX(e.clientX) * duration);
  }, [onSeek, duration, ratioFromClientX]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const ratio = ratioFromClientX(e.clientX);
    if (showHoverIndicator) setHoverRatio(ratio);
    if (draggingRef.current && onSeek && duration > 0 && (e.buttons & 1)) {
      onSeek(ratio * duration);
    }
  }, [showHoverIndicator, onSeek, duration, ratioFromClientX]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) {
      draggingRef.current = false;
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    setHoverRatio(null);
  }, []);

  const showHover = showHoverIndicator && hoverRatio != null && width > 0;
  // ラベルが右端からはみ出さないよう、右寄りでは線の左側に反転配置する
  const hoverLabelOnLeft = showHover && (hoverRatio as number) > 0.8;

  return (
    <Container
      ref={containerRef}
      className={className}
      style={style}
      $height={height}
      $interactive={interactive}
      onPointerDown={interactive ? handlePointerDown : undefined}
      onPointerMove={interactive || showHoverIndicator ? handlePointerMove : undefined}
      onPointerUp={interactive ? handlePointerUp : undefined}
      onPointerLeave={showHoverIndicator ? handlePointerLeave : undefined}
    >
      <Canvas ref={canvasRef} aria-hidden="true" />
      {showHover && (
        <>
          <HoverLine style={{ left: `${(hoverRatio as number) * 100}%` }} />
          {duration > 0 && (
            <HoverLabel
              style={{
                left: `${(hoverRatio as number) * 100}%`,
                transform: hoverLabelOnLeft ? 'translateX(calc(-100% - 4px))' : 'translateX(4px)',
              }}
            >
              {formatAudioTime((hoverRatio as number) * duration)}
            </HoverLabel>
          )}
        </>
      )}
    </Container>
  );
});

WaveformCanvas.displayName = 'WaveformCanvas';

export default WaveformCanvas;
